import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  ArrowsPointingOutIcon,
  BoltIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import {
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

const seamlessCards = [
  {
    eyebrow: 'Slide 1',
    title: 'Remove Friction at Every Step',
    description:
      'TrueLog was designed to eliminate handoffs, follow-ups, and operational noise. By owning the entire logistics lifecycle, we simplify execution and free your teams to focus on higher-value work rather than chasing shipments or managing complexity.',
    icon: Squares2X2Icon,
  },
  {
    eyebrow: 'Slide 2',
    title: 'Move Fast When Timing Matters',
    description:
      'ICT installation rarely allows for delays. Office launches, device rollouts, and regional expansions all run on fixed timelines. TrueLog operates with pace and precision, ensuring your ICT assets move quickly and smoothly when timing is critical.',
    icon: BoltIcon,
  },
  {
    eyebrow: 'Slide 3',
    title: 'Scale Without Adding Complexity',
    description:
      'As organisations grow, logistics often becomes the bottleneck. TrueLog supports growth from a single site to multi-region rollouts, with an operating model that flexes with volume, geography, and complexity.',
    icon: ArrowsPointingOutIcon,
  },
];

const effortlessPoints = [
  'No delays',
  'No complexity',
  'Just execution that works',
];

const WhyTrueLog: React.FC = () => {
  return (
    <div className="bg-white">
      <PageHero
        eyebrow="Why TrueLog"
        title="Move Fast. Remove Friction. Scale with Confidence."
        highlight="Scale with Confidence."
        description="A single, reliable partner that takes ownership of the full ICT logistics journey — turning a fragmented process into one seamless flow."
        heroIcon={ShieldCheckIcon}
        accentIcon={GlobeAltIcon}
        actions={[
          { href: '/contact-us', label: 'Request a Quote' },
          { href: '/contact-us', label: 'Book a Consultation', variant: 'secondary' },
        ]}
      />

      <section className="bg-white py-20">
        <div className="container-custom grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <div>
            <SectionHeading
              eyebrow="The Reality"
              title="ICT logistics has become increasingly complex and risky."
              description="Fragmentation, unpredictable delays, and compliance pressure drain momentum from growing teams."
              align="left"
            />
          </div>

          <div className="space-y-5">
            <p className="text-base leading-8 text-slate-600">
              Managing ICT logistics has become increasingly complex and risky. As organisations expand across regions, the movement and management of ICT hardware often becomes fragmented, difficult to control, and slow to scale.
            </p>
            <p className="text-base leading-8 text-slate-600">
              Teams are often forced to coordinate with multiple vendors, navigate cross-border requirements, and contend with unpredictable delays. Compliance challenges and constant firefighting become part of daily operations. Every delay drains momentum, distracts internal teams, and quietly increases operational risk.
            </p>
            <p className="text-xl font-semibold text-slate-950">TrueLog exists to change that.</p>
            <p className="text-base leading-8 text-slate-600">
              We exist to be a single, reliable partner that takes ownership of the full ICT logistics journey. From cross-border compliance and shipping to installation, returns, and asset lifecycle management, we turn a fragmented process into one seamless flow.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom">
          <SectionHeading
            eyebrow="The Experience"
            title="One Partner. One Seamless Experience."
            description="Managing ICT logistics should not require multiple vendors and endless coordination. TrueLog serves as a single point of ownership across borders, teams, and project phases, delivering a consistent, predictable experience at any scale."
          />

          <div className="grid gap-6 md:grid-cols-3">
            {seamlessCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.08 }}
                className="flex flex-col rounded-[30px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.25)]"
              >
                <div
                  className="inline-flex w-fit rounded-2xl p-3 text-white"
                  style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                >
                  <card.icon className="h-6 w-6" />
                </div>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2447d8]">
                  {card.eyebrow}
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-950">{card.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-600">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="container-custom">
          <div className="mx-auto max-w-4xl text-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span
                className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-white"
                style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
              >
                <SparklesIcon className="h-4 w-4" />
                Make ICT Logistics Effortless
              </span>
              <h2 className="mt-6 font-display text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
                Execution that works. At every scale.
              </h2>
              <p className="mt-5 text-lg leading-8 text-slate-600">
                TrueLog helps organisations move faster, reduce friction, and scale with confidence. No delays. No complexity. Just execution that works.
              </p>
            </motion.div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {effortlessPoints.map((point, index) => (
                <motion.div
                  key={point}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.06 }}
                  className="flex items-center justify-center gap-3 rounded-[24px] border border-slate-200/80 bg-slate-50 p-5"
                >
                  <CheckCircleIcon className="h-5 w-5 text-[#0E9ED5]" />
                  <p className="text-base font-semibold text-slate-900">{point}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 px-4 pb-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[34px] border border-white/[0.12] bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-8 shadow-[0_34px_90px_-48px_rgba(15,23,42,0.9)] sm:p-12">
          <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">Next step</p>
              <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">
                Ready to remove the friction from global ICT logistics?
              </h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                Request a quote for an upcoming rollout, or book a consultation to map out how TrueLog can support your next phase of growth.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/contact-us"
                  className="inline-flex items-center justify-center rounded-2xl px-7 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white shadow-[0_20px_44px_-20px_rgba(14,158,213,0.95)] transition-all duration-300 hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #0E9ED5 0%, #385CF2 100%)' }}
                >
                  Request a Quote
                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                </Link>
                <Link
                  to="/contact-us"
                  className="inline-flex items-center justify-center rounded-2xl border border-white/[0.12] bg-white/[0.08] px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.12]"
                >
                  Book a Consultation
                </Link>
              </div>
            </div>

            <div className="grid gap-3">
              <div className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
                <BoltIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <span>Move fast when timing matters</span>
              </div>
              <div className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
                <Squares2X2Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <span>One partner across borders and phases</span>
              </div>
              <div className="flex items-start gap-3 rounded-[24px] border border-white/10 bg-white/[0.06] px-4 py-4 text-sm text-slate-200">
                <ArrowsPointingOutIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                <span>Scale without adding complexity</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyTrueLog;
